import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { listPosts } from "../lib/api";
import { artistName } from "../lib/artists";
import { PostCard, Lightbox } from "../components/sections/Portfolio";

const PAGE_SIZE = 12;

export default function ArtistPortfolio() {
  const { artistId } = useParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [lightbox, setLightbox] = useState(null);
  const name = artistName(artistId);

  useEffect(() => {
    window.scrollTo(0, 0);
    const prevTitle = document.title;
    document.title = `${name} — Portfolio · JAY-INK Academy`;
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevHref = canonical ? canonical.getAttribute("href") : null;
    if (canonical) canonical.setAttribute("href", `https://jayink.academy/portfolio/${artistId}`);
    return () => {
      document.title = prevTitle;
      if (canonical && prevHref) canonical.setAttribute("href", prevHref);
    };
  }, [name, artistId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await listPosts({ artist: artistId, limit: 200 });
        setPosts(data);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [artistId]);

  const visible = useMemo(() => posts.slice(0, visibleCount), [posts, visibleCount]);

  const openLightbox = (post, index = 0) => setLightbox({ post, index });
  const closeLightbox = () => setLightbox(null);
  const prevImg = () =>
    setLightbox((l) =>
      l ? { ...l, index: (l.index - 1 + l.post.images.length) % l.post.images.length } : l
    );
  const nextImg = () =>
    setLightbox((l) => (l ? { ...l, index: (l.index + 1) % l.post.images.length } : l));

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prevImg();
      if (e.key === "ArrowRight") nextImg();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <main data-testid="artist-portfolio-page" className="min-h-screen bg-[#F5F5F5] text-[#0A0A0A]">
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/85 backdrop-blur-md border-b border-[#E5E5E5]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <a
            href="/"
            data-testid="artist-portfolio-logo"
            className="text-xs md:text-base font-medium tracking-[0.22em] md:tracking-[0.28em] uppercase text-[#0A0A0A]"
          >
            JAY-INK&nbsp;ACADEMY
          </a>
          <a
            href="/#portfolio"
            data-testid="artist-portfolio-back"
            className="text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A] pb-1 hover:opacity-60"
          >
            ← Back to Portfolio
          </a>
        </div>
      </header>

      <section className="pt-36 md:pt-48 pb-24 md:pb-32 px-6 md:px-12">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-14 md:mb-20">
            <div className="overline mb-4 text-[#666]">Portfolio</div>
            <h1
              className="font-display text-[#0A0A0A] uppercase mx-auto"
              style={{
                fontSize: "clamp(2.2rem, 7vw, 6rem)",
                letterSpacing: "0.28em",
                paddingLeft: "0.28em",
                fontWeight: 200,
                lineHeight: 1,
              }}
              data-testid="artist-portfolio-title"
            >
              {name}
            </h1>
            {!loading && (
              <p className="mt-6 text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#999]">
                {posts.length} project{posts.length === 1 ? "" : "s"}
              </p>
            )}
          </div>

          {loading ? (
            <div className="py-24 text-center text-[#666] text-sm tracking-widest uppercase">Loading…</div>
          ) : posts.length === 0 ? (
            <div className="py-24 text-center text-[#666] text-sm tracking-widest uppercase" data-testid="artist-portfolio-empty">
              No published work yet.
            </div>
          ) : (
            <>
              <div
                data-testid="artist-portfolio-grid"
                className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 md:gap-6"
              >
                {visible.map((post, i) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    index={i}
                    onOpen={(idx) => openLightbox(post, idx)}
                  />
                ))}
              </div>
              {visibleCount < posts.length && (
                <div className="mt-10 text-center">
                  <button
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    data-testid="artist-portfolio-load-more"
                    className="inline-block text-[10px] md:text-xs tracking-[0.28em] uppercase px-8 py-3 border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
                  >
                    Load More
                  </button>
                </div>
              )}
              <div className="mt-16 text-center">
                <a
                  href="/#portfolio"
                  data-testid="artist-portfolio-back-bottom"
                  className="text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A] pb-1 hover:opacity-60"
                >
                  ← Back to Portfolio
                </a>
              </div>
            </>
          )}
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <Lightbox
            post={lightbox.post}
            index={lightbox.index}
            onClose={closeLightbox}
            onPrev={prevImg}
            onNext={nextImg}
          />
        )}
      </AnimatePresence>
    </main>
  );
}
